/**
 * @file Renders the status of a transaction as a colored tag, with pending approval and
 * retry handled as their own statuses.
 */
import React from "react";
import { Tag } from "antd";

import {
  HOLD,
  ERROR,
  FAILED,
  PENDING,
  REJECTED,
  PROCESSED,
  CANCELLED,
} from "@api/services/transactions/shapes/TransactionStatusEnum";

export type StatusTransaction = {
  status: string;
  isApproved?: boolean;
  retryTransactionId?: string;
};

type Props = {
  transaction: StatusTransaction;
};

const RETRIED = "RETRIED";
const PENDING_APPROVAL = "PENDING_APPROVAL";

const COLORS_MAP: Record<string, string | null> = {
  [HOLD]: "orange",
  [ERROR]: "red",
  [FAILED]: "red",
  [RETRIED]: null,
  [PENDING]: "blue",
  [REJECTED]: "red",
  [CANCELLED]: null,
  [PROCESSED]: "green",
  [PENDING_APPROVAL]: "orange",
};

const LABELS_MAP: Record<string, string> = {
  [HOLD]: "Hold",
  [ERROR]: "Error",
  [FAILED]: "Failed",
  [RETRIED]: "Retried",
  [PENDING]: "Processing",
  [REJECTED]: "Rejected",
  [PROCESSED]: "Processed",
  [CANCELLED]: "Cancelled",
  [PENDING_APPROVAL]: "Pending Approval",
};

/**
 * Gives the display label of the status, or the status itself when it has none.
 */
const getTransactionStatusLabel = (status: string): string => LABELS_MAP[status] || status;

/**
 * Gives the tag color of the status.
 */
const getTransactionStatusColor = (status: string): string | null | undefined => COLORS_MAP[status];

/**
 * Derives the display status of the transaction: pending approval for a pending or a
 * hold transaction that is not approved, retried for one with a retry, or its own status.
 */
const computeStatus = (transaction: StatusTransaction): string => {
  const { status, isApproved, retryTransactionId } = transaction;

  const isHold = status === HOLD;
  const isPending = status === PENDING;
  const isPendingApproval = (isPending || isHold) && !isApproved;

  const isRetried = !!retryTransactionId;

  if (isPendingApproval) {
    return PENDING_APPROVAL;
  }

  if (isRetried) {
    return RETRIED;
  }

  return status;
};

/**
 * Renders the computed status of the transaction, with its label and its color.
 */
const TransactionStatusTag = ({ transaction }: Props): JSX.Element => {
  const status = computeStatus(transaction);

  const label = getTransactionStatusLabel(status);
  const color = getTransactionStatusColor(status);

  return (
    <Tag
      color={color}
      bordered={false}
    >
      {label}
    </Tag>
  );
};

export default TransactionStatusTag;

export {
  computeStatus,
  getTransactionStatusLabel,
  getTransactionStatusColor,
};
